import React, { useState, useEffect } from 'react';
import { Grid, TextField, Typography, FormControlLabel, Checkbox, Link, Alert, useTheme } from '@mui/material'
import InputAdornment from '@mui/material/InputAdornment';
import SignInButton from './SignInButton';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { logIntoHost } from '../../../ts/Common';
import { t } from 'i18next';
import { Trans } from 'react-i18next';

const MY_HOST = import.meta.env.VITE_MY_HOST || "https://spectre-licensing.com"
const DOMAIN = import.meta.env.VITE_DOMAIN || '.spectre-licensing.com'

const styles = {
    textComponent: {
        backgroundColor: 'var(--spectre-surface)',
        // mt: 1
    },
    emailTextArea: {
        color: 'var(--spectre-text-secondary)',
        backgroundColor: 'var(--spectre-input)',
        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
            borderColor: '#F7914B',
            borderWidth: 2,
        },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px var(--spectre-input) inset',
            WebkitTextFillColor: 'var(--spectre-text-primary)',
            caretColor: 'var(--spectre-text-primary)',
        },
        emailAdornment: {
            color: 'var(--spectre-text-muted)',
        },
    },
    passwordTextArea: {
        color: 'var(--spectre-text-secondary)',
        backgroundColor: 'var(--spectre-input)',
        mt: 2,
        '& .MuiOutlinedInput-root.Mui-focused fieldset': {
            borderColor: '#F7914B',
            borderWidth: 2,
        },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: '0 0 0 1000px var(--spectre-input) inset',
            WebkitTextFillColor: 'var(--spectre-text-primary)',
            caretColor: 'var(--spectre-text-primary)',
        },
        passwordAdornment: {
            color: 'var(--spectre-text-muted)',
        },
    },
    forgotPassword: {
        fontWeight: 700,
        fontSize: 16,
        color: 'var(--spectre-text-primary)',
        pb: 5,
        pt: 1,
        float: 'left',
    },
    agreementCheckBox: {
        backgroundColor: 'var(--spectre-surface)',
        textArea: {
            color: 'var(--spectre-text-muted)'
        },
        mt: 1,
    },
    textAgreement: {
        color: 'var(--spectre-text-primary)',
        float: 'left',
        mt: 1.2,
        fontSize: 14,
        pl: {
            xs: 1,
            sm: 1,
            md: 0
        }
    },
    CircularProgress: {
        mr: 1,
        color: "#FFFFFF"
    },
    InvalidLogin: {
        fontWeight: 750,
        color: '#D43636',
        display: 'list-item',
    }
}

export default function EmailAndPasswordForm(props: any) {
    const { setIsLoggedIn, signInProgress, setSignInProgress, setUser, removeCookie, showLoginError, setShowLoginError, loginErrorText, setLoginErrorText } = props;
    const [agreementChecked, setAgreementChecked] = useState<boolean>(false);
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [showPassword, setShowPassword] = useState(false);
    const [enableSignIn, setEnableSignIn] = useState<boolean>(false);
    const [, setEncodedEmailAndPassword] = useState<string>("");
    const theme = useTheme();

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: any) => {
        event.preventDefault();
    };

    const setUserNameAndPasswordEncoded = async function () {
        removeCookie('user64', { path: '/', domain: DOMAIN })
        var encodedCredentials = btoa(`${email}:${password}`)
        setEncodedEmailAndPassword(encodedCredentials);
        setSignInProgress(true);

        logIntoHost(encodedCredentials, `${MY_HOST}/api/Account/LoginPWA`)
            .then((response: any) => {
                if (response.ok) {
                    setShowLoginError("none");
                    setIsLoggedIn(true);
                    setSignInProgress(false);
                    setUser(email);
                } else {
                    console.log("Login failure")
                    setSignInProgress(false);
                    setShowLoginError('block');
                    setLoginErrorText('Invalid email or password');
                }
            })
            .catch((err: any) => {
                setShowLoginError("block");
                console.log(err);
                setSignInProgress(false);
                setLoginErrorText("Error logging in, please try again");
            })
    }

    useEffect(() => {
        if (agreementChecked && email && password) {
            setEnableSignIn(true)
        } else {
            setEnableSignIn(false)
        }
    }, [agreementChecked, email, password])

    return (
        <React.Fragment>
            <Grid item lg={7} md={7} xs={11} sx={styles.textComponent}>
                <TextField id="login_username" variant="outlined" fullWidth sx={styles.emailTextArea} value={email}
                    onChange={(element) => {
                        setEmail(element.target.value);
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment sx={styles.emailTextArea.emailAdornment} position="start">
                            <Typography sx={styles.emailTextArea.emailAdornment}>{t('loginPage.emailAndPasswordForm.email')} |</Typography>
                        </InputAdornment>
                    }}
                />
            </Grid>
            <Grid item lg={7} md={7} xs={11} sx={styles.textComponent}>
                <TextField id="login_password" variant="outlined" fullWidth sx={styles.passwordTextArea} type={showPassword ? "text" : "password"} value={password}
                    onChange={(element) => {
                        setPassword(element.target.value);
                    }}
                    InputProps={{
                        startAdornment: <InputAdornment sx={styles.passwordTextArea.passwordAdornment} position="start">
                            <Typography sx={styles.passwordTextArea.passwordAdornment}>{t('loginPage.emailAndPasswordForm.password')} |</Typography>
                        </InputAdornment>,
                        endAdornment: <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    }}
                />
            </Grid>
            <Grid item lg={7} md={7} xs={11}>
                <Link href="https://spectre-licensing.com/Account/ForgotPassword">
                    <Typography sx={styles.forgotPassword}>{t('loginPage.emailAndPasswordForm.forgotPassword')}</Typography>
                </Link>
            </Grid>
            <Grid item md={7} xs={11} >
                <Typography sx={styles.textAgreement}>
                    <FormControlLabel
                        style={{ width: "4%", color: 'var(--spectre-text-secondary)' }}
                        sx={{ pr: 2.5 }}
                        control={
                            <Checkbox
                                sx={theme.palette.mode === 'dark' ? {
                                    color: theme.palette.spectre.textPrimary,
                                    '&.Mui-checked': {
                                        color: theme.palette.spectre.textPrimary,
                                    },
                                } : undefined}
                            />
                        }
                        label=""
                        checked={agreementChecked}
                        onChange={(element: any) => { setAgreementChecked(element.target.checked) }}
                    />                   
                    <Trans
                        i18nKey="loginPage.emailAndPasswordForm.termsAgreement"
                        components={[
                        <></>,
                        <Link
                            key="terms-link"
                            href="https://spectre-licensing.com/Home/GeneralTermsConditions"
                            sx={{
                                color: 'var(--spectre-text-primary)',
                                textDecoration: 'underline',
                            }}
                        />
                        ]}
                    />
                </Typography>
            </Grid>
            <SignInButton signInProgress={signInProgress} enableSignIn={enableSignIn} setUserNameAndPasswordEncoded={setUserNameAndPasswordEncoded}></SignInButton>
            <Grid item md={7} xs={11} sx={{ display: showLoginError }}>
                <Alert sx={{mt: 2}} severity="error">{loginErrorText}</Alert>
            </Grid>
        </React.Fragment>
    )
}